/**
 * The walk - a move as the display plays it (plan design-gaps, M1).
 *
 * The token record is the COMMITTED position; a move is an event on the log
 * (`moveBeat`), and each screen derives from it how the figure got there:
 * the route cut into beats of WALK_SEGMENT_M over the ground, each eased
 * over the mode's time, timed from when THIS screen saw the line (as the
 * beats are, so a late screen walks late rather than never). A short last
 * leg keeps the pace: the beats are shortened together, never one stub.
 * Pure: no clock, no React, no randomness.
 */
import type { MoveBeat, MoveMode, MovePoint } from '../moveBeat';
import { groundDistance } from '../battlefield/measure';
import { easeInOut, lerp } from './timeline';

export interface WalkPoint {
  x: number;
  z: number;
  /** The height walked at: a climb mid-route lerps it. */
  y: number;
}

interface WalkSegment {
  from: WalkPoint;
  to: WalkPoint;
  ms: number;
}

export interface Walk {
  id: string;
  combatantId: string;
  /** When THIS screen started playing it (the display clock's time). */
  at: number;
  segments: WalkSegment[];
  /** The whole walk's length in ms. */
  ms: number;
}

/** One beat of walking covers this much ground. */
export const WALK_SEGMENT_M = 1.6;

/** One beat's time by mode (the user's ruling, 2026-10-11): a walk at 520 ms, a run or charge at 320; a Pinned crawl at 900. */
export const WALK_SEGMENT_MS: Record<MoveMode, number> = { half: 520, full: 520, run: 320, charge: 320, crawl: 900 };

/** How long a screen keeps a move it heard of: longer than any walk (a 48-point route at a walking pace). */
export const WALK_KEEP_MS = 30_000;
/** Under this much ground a move is a fidget, not a walk. */
const STILL_M = 0.05;

/** Each body of a horde sets off this much later than the one before (plan design-gaps, M4). */
const HORDE_BODY_MS = 80;

const point = (p: MovePoint): WalkPoint => ({ x: p.x, z: p.z, y: p.y ?? 0 });

/** Ground distance: a climb costs movement by the rules, not walking time. */
const ground = groundDistance;

/** `b` itself at k = 1, so a beat ends exactly on its point. */
function lerpPoint(a: WalkPoint, b: WalkPoint, k: number): WalkPoint {
  if (k >= 1) return b;
  return { x: lerp(a.x, b.x, k), z: lerp(a.z, b.z, k), y: lerp(a.y, b.y, k) };
}

/** The route as points, a point the route repeats dropped. */
function routeOf(move: MoveBeat): WalkPoint[] {
  const route: WalkPoint[] = [];
  for (const p of [move.from, ...move.path, move.to]) {
    const next = point(p);
    const last = route[route.length - 1];
    if (!last || ground(last, next) >= STILL_M) route.push(next);
  }
  return route;
}

/** One leg cut into equal beats: as many as the ground needs, each timed by its share of a full beat. */
function legSegments(a: WalkPoint, b: WalkPoint, beatMs: number): WalkSegment[] {
  const length = ground(a, b);
  const beats = Math.max(1, Math.ceil(length / WALK_SEGMENT_M));
  const ms = Math.round((length / beats / WALK_SEGMENT_M) * beatMs);
  return Array.from({ length: beats }, (_, i) => ({
    from: lerpPoint(a, b, i / beats),
    to: lerpPoint(a, b, (i + 1) / beats),
    ms,
  }));
}

/**
 * The walk for a move (its beat, engine/moveBeat), timed from `seenAt` (default: when the move was made). Null
 * for a move that goes nowhere.
 */
export function walkOf(move: MoveBeat, seenAt: number = move.at): Walk | null {
  const route = routeOf(move);
  if (route.length < 2) return null;
  const beatMs = WALK_SEGMENT_MS[move.mode];
  const segments = route.slice(1).flatMap((to, i) => legSegments(route[i], to, beatMs));
  return {
    id: `${move.actorId}:${move.at}`,
    combatantId: move.actorId,
    at: seenAt,
    segments,
    ms: segments.reduce((sum, s) => sum + s.ms, 0),
  };
}

/** Where the figure is at the clock's `now`: the origin before the walk, the destination after, eased within a beat. */
export function walkAt(walk: Walk, now: number): WalkPoint {
  let t = now - walk.at;
  for (const s of walk.segments) {
    if (t < s.ms) return lerpPoint(s.from, s.to, easeInOut(Math.max(0, t) / s.ms));
    t -= s.ms;
  }
  return walk.segments[walk.segments.length - 1].to;
}

/** The i-th body of a horde sets off this much after the first. */
export const hordeOffsetMs = (i: number) => i * HORDE_BODY_MS;

/** The token as it stands mid-walk: at the walk's point, standing at the height it walks at (plan design-gaps, M3). */
export function placeWalking<T extends { x: number; z: number; elevation: number }>(token: T, point: WalkPoint): T {
  return { ...token, x: point.x, z: point.z, elevation: point.y };
}
